/**
 * Otto Extension Design Tokens
 *
 * Semantic tokens for Otto-specific UI concepts that Fluent UI v9 doesn't cover.
 * These tokens are CSS custom properties injected per-theme via the FluentProvider wrapper.
 *
 * Token naming convention follows Fluent's pattern:
 *   --otto-{category}-{element}-{variant}-{state}
 *
 * Categories:
 *   canvas   - workflow designer canvas
 *   node     - workflow node types
 *   note     - note node styling
 *   overlay  - modal/dialog backdrop overlays
 *   settings - settings modal component metrics
 *   editor   - CodeMirror editor chrome (active line, selection, search)
 *   code     - CodeMirror syntax highlighting
 *   status   - operation status colors (beyond Fluent palette)
 *   landing  - marketing landing page surfaces
 *
 * Usage:
 *   import { ottoTokens } from '@/theme/ottoTokens';
 *
 *   const useStyles = makeStyles({
 *     root: { backgroundColor: ottoTokens.canvasBackground },
 *   });
 */

export const ottoTokens = {
  // ─── Canvas ────────────────────────────────────────────────────────
  canvasBackground: 'var(--otto-canvas-bg)',
  canvasGridColor: 'var(--otto-canvas-grid)',
  canvasSelectionStroke: 'var(--otto-canvas-selection-stroke)',
  canvasSelectionFill: 'var(--otto-canvas-selection-fill)',
  canvasMinimapNote: 'var(--otto-canvas-minimap-note)',
  canvasMinimapMask: 'var(--otto-canvas-minimap-mask)',
  /** Single-layer shadow used by clip-path node shapes. */
  nodeElevationShadow: 'var(--otto-node-elevation-shadow)',
  /** Faint stripe overlay used by running timeline segments. */
  runningStripe: 'var(--otto-running-stripe)',
  /** Translucent darkening overlay for controls on dynamic node-brand fills. */
  nodePillHoverOverlay: 'var(--otto-node-pill-hover-overlay)',

  // ─── Node type accent colors ──────────────────────────────────────
  /** Trigger nodes (e.g., HTTP request, schedule) */
  nodeAccentTrigger: 'var(--otto-node-accent-trigger)',
  /** Standard action nodes */
  nodeAccentAction: 'var(--otto-node-accent-action)',
  /** Agent / AI nodes */
  nodeAccentAgent: 'var(--otto-node-accent-agent)',
  /** Control flow nodes (condition, switch, foreach) */
  nodeAccentControlFlow: 'var(--otto-node-accent-control-flow)',
  /** Tool nodes */
  nodeAccentTool: 'var(--otto-node-accent-tool)',
  /** Model / AI model nodes */
  nodeAccentModel: 'var(--otto-node-accent-model)',

  // ─── Note node ────────────────────────────────────────────────────
  noteBackground: 'var(--otto-note-bg)',
  noteForeground: 'var(--otto-note-fg)',
  noteBorderSelected: 'var(--otto-note-border-selected)',

  // ─── Overlay / backdrop ─────────────────────────────────────────────
  /** Modal/dialog backdrop overlay */
  overlayBackdrop: 'var(--otto-overlay-backdrop)',

  // ─── Settings modal ─────────────────────────────────────────────────
  settingsNavIconSize: 'var(--otto-settings-nav-icon-size)',

  // ─── Global nav palette ─────────────────────────────────────────────
  /** Row icon size for the ⌘K command palette */
  paletteRowIconSize: 'var(--otto-palette-row-icon-size)',

  // ─── Code editor chrome ───────────────────────────────────────────
  /** Active line highlight background */
  editorActiveLine: 'var(--otto-editor-active-line)',
  /** Text selection background */
  editorSelection: 'var(--otto-editor-selection)',
  /** Focused text selection background */
  editorSelectionFocused: 'var(--otto-editor-selection-focused)',
  /** Search match highlight */
  editorSearchMatch: 'var(--otto-editor-search-match)',
  /** Active search match highlight */
  editorSearchMatchSelected: 'var(--otto-editor-search-match-selected)',

  // ─── Code editor syntax highlighting ──────────────────────────────
  codeSyntaxString: 'var(--otto-code-string)',
  codeSyntaxNumber: 'var(--otto-code-number)',
  codeSyntaxBoolean: 'var(--otto-code-boolean)',
  codeSyntaxNull: 'var(--otto-code-null)',
  codeSyntaxPropertyName: 'var(--otto-code-property-name)',
  codeSyntaxPunctuation: 'var(--otto-code-punctuation)',
  codeSyntaxKeyword: 'var(--otto-code-keyword)',
  codeSyntaxControlKeyword: 'var(--otto-code-control-keyword)',
  codeSyntaxFunction: 'var(--otto-code-function)',
  codeSyntaxVariable: 'var(--otto-code-variable)',
  codeSyntaxDefinition: 'var(--otto-code-definition)',
  codeSyntaxComment: 'var(--otto-code-comment)',
  codeSyntaxOperator: 'var(--otto-code-operator)',
  codeSyntaxTypeName: 'var(--otto-code-type-name)',
  codeSyntaxRegexp: 'var(--otto-code-regexp)',

  // ─── Agent variant pastel badges ─────────────────────────────────
  // Pastel background + foreground pairs for the four Agent variants
  // (Native, Foundry, Coding, Hosted) used by picker cards and connection
  // rows in AgentTypeView. Lighter than Fluent's `colorPaletteXxxBackground2`
  // so the badges read as airy accents rather than saturated chips.
  agentNativeBadgeBackground: 'var(--otto-agent-native-badge-bg)',
  agentNativeBadgeForeground: 'var(--otto-agent-native-badge-fg)',
  agentFoundryBadgeBackground: 'var(--otto-agent-foundry-badge-bg)',
  agentFoundryBadgeForeground: 'var(--otto-agent-foundry-badge-fg)',
  agentCodingBadgeBackground: 'var(--otto-agent-coding-badge-bg)',
  agentCodingBadgeForeground: 'var(--otto-agent-coding-badge-fg)',
  agentHostedBadgeBackground: 'var(--otto-agent-hosted-badge-bg)',
  agentHostedBadgeForeground: 'var(--otto-agent-hosted-badge-fg)',

  /**
   * Saturated brand identity colors per agent variant. Used as the
   * picker tile background (neutral surface, benefits from stronger
   * fill) — distinct from the pastel badge tokens above which are
   * tuned for chips on the same neutral surface.
   */
  agentNativeBrand: 'var(--otto-agent-native-brand)',
  agentFoundryBrand: 'var(--otto-agent-foundry-brand)',
  agentCodingBrand: 'var(--otto-agent-coding-brand)',
  agentHostedBrand: 'var(--otto-agent-hosted-brand)',

  // ─── Status indicators (run status, beyond Fluent palette) ────────
  statusSucceeded: 'var(--otto-status-succeeded)',
  statusFailed: 'var(--otto-status-failed)',
  statusRunning: 'var(--otto-status-running)',
  statusWaiting: 'var(--otto-status-waiting)',
  statusCancelled: 'var(--otto-status-cancelled)',
  statusSkipped: 'var(--otto-status-skipped)',
  statusTimedOut: 'var(--otto-status-timed-out)',
  statusPending: 'var(--otto-status-pending)',

  // ─── Marketing landing page ───────────────────────────────────────
  /** Radial ambient glow layered behind the hero (transparent at edges). */
  landingHeroAmbient: 'var(--otto-landing-hero-ambient)',
  /** 135° brand → cyan gradient. Primary CTA buttons, logo mark fill,
   *  stack-viz top layer, throttle "flowing" lane, scenario step circles. */
  landingPrimaryGradient: 'var(--otto-landing-primary-gradient)',
  /** Soft brand wash for the Alex-scenario card background. */
  landingScenarioBackground: 'var(--otto-landing-scenario-bg)',
  /** Soft brand wash for the final CTA card background. */
  landingFinalCtaBackground: 'var(--otto-landing-final-cta-bg)',
  /** Elevated card surface distinct from `colorNeutralBackground3`. */
  landingCardBackground: 'var(--otto-landing-card-bg)',
  /** Even dimmer alias for monospaced URL strings under CTAs. */
  landingDimText: 'var(--otto-landing-dim-text)',
  /** Cyan accent text used for eyebrow labels and inline emphasis. */
  landingAccentTextCool: 'var(--otto-landing-accent-text-cool)',
  /** Approval-mock icon backgrounds. */
  landingApprovalIconOk: 'var(--otto-landing-approval-icon-ok)',
  landingApprovalIconWarn: 'var(--otto-landing-approval-icon-warn)',
  landingApprovalIconIdle: 'var(--otto-landing-approval-icon-idle)',
  /** Throttle-viz "blocked" bar gradient (warm). */
  landingBlockedGradient: 'var(--otto-landing-blocked-gradient)',
  /** Subtle accent border for emphasised throttle/flowing lane. */
  landingAccentBorder: 'var(--otto-landing-accent-border)',
  /** Always-dark text colour for use on the primary brand gradient.
   *  The gradient endpoints are bright in both themes, so we need a
   *  static near-black foreground to stay AA-readable instead of
   *  flipping with the theme. */
  landingPrimaryGradientForeground: 'var(--otto-landing-primary-gradient-fg)',
} as const;

/** Type-safe token name type */
export type OttoToken = keyof typeof ottoTokens;

/**
 * All CSS custom property names used by Otto tokens.
 * Useful for constructing style dictionaries or debugging.
 */
export const ottoTokenCssVars = Object.values(ottoTokens).map(
  (v) => v.slice(4, -1) // strip 'var(' prefix and ')' suffix
);
